import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('primaryPhysician');
export interface PrimaryPhysicianInfoRec {
  id: string;
  userId: string;
  physicianName?: string;
  practiceName?: string;
  phoneNumber?: string;
  faxNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  yearsAsPatient?: number;
  lastVisitDate?: string;
  nextAppointmentDate?: string;
  medicalRecordNumber?: string;
  insuranceAccepted?: string;
  referralRequired?: boolean;
  emergencyContact?: boolean;
  portalUsername?: string;
  portalPassword?: string;
  portalUrl?: string;
  notes?: string;
  isLocked?: boolean;
}

class PrimaryPhysicianInfoService {
  async getAllPrimaryPhysicianInfo(targetUserId?: string): Promise<PrimaryPhysicianInfoRec[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await http.get(`/api/primary-physician-info${params}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch primary physician information');
    }

    return response.data.data || [];
  }

  async getPrimaryPhysicianInfo(targetUserId?: string): Promise<PrimaryPhysicianInfoRec[]> {
    return this.getAllPrimaryPhysicianInfo(targetUserId);
  }

  async createPrimaryPhysicianInfo(data: Partial<PrimaryPhysicianInfoRec>): Promise<PrimaryPhysicianInfoRec> {
    const response = await http.post('/api/primary-physician-info', data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save primary physician information');
    }

    return response.data.data;
  }

  async updatePrimaryPhysicianInfo(id: string, data: Partial<PrimaryPhysicianInfoRec>): Promise<PrimaryPhysicianInfoRec> {
    const response = await http.patch(`/api/primary-physician-info/${id}`, data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update primary physician information');
    }

    return response.data.data;
  }

  async deletePrimaryPhysicianInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/primary-physician-info/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete primary physician information');
    }
  }

  async lockPrimaryPhysicianInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for primary physician ID:', id);
    const response = await http.post(`/api/primary-physician-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock primary physician information');
    }
  }

  async unlockPrimaryPhysicianInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/primary-physician-info/${id}/unlock`, { granteeEmails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock primary physician information');
    }
  }

  async getPrimaryPhysicianAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/primary-physician-info/${id}/access-grants`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const primaryPhysicianInfoService = new PrimaryPhysicianInfoService();